/**
 * ERC No-Connect Validator Adapter for DRC System
 *
 * This validator checks for no-connect pins that are actually connected.
 * It detects no-connect pins by:
 * 1. Pin type set to NO_CONNECT
 * 2. Pin name or pin number matching "NC" patterns (e.g., "NC", "N.C.", "NC1")
 *
 * Feature-gated via DrcEnabledCheckers.ErcNoConnectValidator
 */
import { ReviewType } from "@buildwithflux/models";
import { getProblemTypeKeyFromReviewType } from "../../drc/types";
import { ElectricalPinType } from "../types";
import { getPinsForNet, createErcProblem, formatPinReference } from "./ercUtils";
/**
 * Checks if a string matches common no-connect naming patterns.
 * Patterns: "NC", "N.C.", "N/C", "NC1", "NC2", etc.
 */
function isNoConnectName(name) {
    if (!name)
        return false;
    const normalized = name.trim().toUpperCase();
    // Exact match for common NC patterns
    if (normalized === "NC" || normalized === "N.C." || normalized === "N/C") {
        return true;
    }
    // Match "NC" followed by optional numbers (e.g., NC1, NC2, NC10)
    if (/^NC\d*$/.test(normalized)) {
        return true;
    }
    return false;
}
/**
 * Checks if a pin should be treated as a no-connect pin.
 * This includes pins with NO_CONNECT type or pins with NC naming patterns.
 */
function isNoConnectPin(pin, inputs) {
    // Check pin type first
    if (pin.pinType === ElectricalPinType.NO_CONNECT) {
        return true;
    }
    // Check pin name and pin number for NC patterns
    const element = inputs.elements[pin.elementUid];
    const terminal = element?.part_version_data_cache?.terminals?.[pin.terminalUid];
    if (terminal) {
        if (isNoConnectName(terminal.name) || isNoConnectName(terminal.pin_number)) {
            return true;
        }
    }
    return false;
}
/**
 * Validator that checks for no-connect pins that are connected to nets.
 */
export class ErcNoConnectValidator {
    reviewType = ReviewType.DesignRuleCheckErcNoConnect;
    problemTypeKey = getProblemTypeKeyFromReviewType(this.reviewType);
    defaultHidden = false;
    async checkForProblems(inputs) {
        const foundProblems = [];
        for (const [, terminals] of Object.entries(inputs.netMap)) {
            const pins = getPinsForNet(inputs, terminals);
            if (pins.length <= 1) {
                continue;
            }
            // Check for no-connect pins in nets with multiple pins
            for (const pin of pins) {
                if (isNoConnectPin(pin, inputs)) {
                    foundProblems.push(createErcProblem(this.problemTypeKey, `${this.problemTypeKey}_${pin.elementUid}_${pin.terminalUid}`, `No-connect pin connected: ${formatPinReference(pin)}`, [pin.elementUid]));
                }
            }
        }
        return {
            error: false,
            problemTypeKey: this.problemTypeKey,
            foundProblems,
        };
    }
}
//# sourceMappingURL=ErcNoConnectValidator.js.map